import React from "react"
import { Link } from "gatsby"
import {
  SemiBdTitle3,
  SemiBdTitle4,
  IconChevronRight,
  SanityImg,
  BlockContentInline,
} from "@sr/gatsby-theme-ui"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"
import SanityImage from "gatsby-plugin-sanity-image"

const alloyBg =
  "https://res.cloudinary.com/smartrent-com/image/upload/v1692347404/pages/product/ProductVector_cogvni.svg"

export interface ProductEntryHardwareProps {
  hardware: {
    on: boolean
    header: string
    _rawBody: any
    ctaLink: {
      reference: {
        path: {
          current: string
        }
      }
    }
    ctaText: string
    hardwareImgList: {
      img: SanityImg
    }[]
  }
}

const ProductEntryHardware = ({ hardware }: ProductEntryHardwareProps) => {
  if (
    !hardware?.on ||
    !hardware?.header ||
    !hardware?._rawBody ||
    !hardware?.hardwareImgList.length
  )
    return null

  return (
    <ProductEntryHardwareStyled>
      <div className="product-entry-hardware__container">
        <img className="product-entry-hardware__bg" src={alloyBg} />
        <div className="product-entry-hardware__text-container">
          <SemiBdTitle3 className="product-entry-hardware__header">
            {hardware.header}
          </SemiBdTitle3>
          <BlockContentInline blocks={hardware._rawBody} />
          <div className="product-entry-hardware__link-container">
            <Link
              to={hardware.ctaLink?.reference?.path?.current}
              className="product-entry-hardware__link"
            >
              <div className="product-entry-hardware__link-icon-container">
                <IconChevronRight color="var(--color-primary)" />
              </div>
              <SemiBdTitle4 className="product-entry-hardware__link-text">
                {hardware.ctaText ? `${hardware.ctaText}` : "Learn More"}
              </SemiBdTitle4>
            </Link>
          </div>
        </div>
        <div className="product-entry-hardware__cards-container">
          {hardware?.hardwareImgList &&
            hardware.hardwareImgList.map((image: any, i: number) => {
              return (
                <div className="product-entry-hardware__card-container" key={i}>
                  <SanityImage
                    className="product-entry-hardware__card-img"
                    alt={image.img.alt || ""}
                    asset={image.img.asset}
                    crop={image.img.crop}
                    hotspot={image.img.hotspot}
                    width={1500}
                  />
                </div>
              )
            })}
        </div>
      </div>
    </ProductEntryHardwareStyled>
  )
}

export default ProductEntryHardware

const ProductEntryHardwareStyled = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${rem(144)};
  background: var(--color-gray075);

  .product-entry-hardware__container {
    display: flex;
    flex-direction: column;
    padding: ${rem(64)} ${rem(16)} 0;
    position: relative;

    @media ${media.midUp} {
      padding-top: ${rem(80)};
      justify-content: center;
      align-items: center;
      text-align: center;
    }

    @media ${media.stMidUp} {
      padding-top: ${rem(64)};
      max-width: ${rem(1440)};
      flex-direction: row;
      gap: ${rem(37)};
      text-align: left;
    }
  }

  .product-entry-hardware__bg {
    display: none;

    @media ${media.midUp} {
      display: block;
      position: absolute;
      height: ${rem(162)};
      object-fit: cover;
      object-position: left;
      bottom: ${rem(40)};
      right: ${rem(0)};
      z-index: 1;
    }

    @media ${media.stMidUp} {
      width: ${rem(868)};
      height: auto;
      object-fit: contain;
      right: ${rem(-280)};
      bottom: ${rem(26)};
    }
  }

  .product-entry-hardware__text-container {
    display: flex;
    flex-direction: column;
    z-index: 2;
    @media ${media.midUp} {
      justify-content: center;
      align-items: center;
    }

    @media ${media.stMidUp} {
      justify-content: flex-start;
      align-items: flex-start;
      padding: ${rem(32)} 0 ${rem(128)};
    }
  }

  .product-entry-hardware__header {
    max-width: ${rem(342)};

    @media ${media.midUp} {
      font-size: ${rem(40)};
      line-height: ${rem(48)};
      max-width: ${rem(480)};
    }

    @media ${media.stMidUp} {
      max-width: ${rem(424)};
    }
  }

  p {
    padding-top: ${rem(16)};
    font-size: ${rem(16)};

    @media ${media.midUp} {
      max-width: ${rem(616)};
    }

    @media ${media.stMidUp} {
      max-width: ${rem(389)};
    }
  }

  .product-entry-hardware__link-container {
    display: flex;
    padding-top: ${rem(40)};

    @media ${media.midUp} {
      padding-top: ${rem(32)};
    }

    @media ${media.stMidUp} {
      padding-top: ${rem(32)};
    }

    .product-entry-hardware__link {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: ${rem(8)};
      color: var(--color-primary);
      text-decoration: none;
    }

    .product-entry-hardware__link-icon-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(32)};
      height: ${rem(32)};
      border: ${rem(1)} solid var(--color-primary);
      border-radius: ${rem(32)};
    }

    .product-entry-hardware__link-text {
      @media ${media.stMidUp} {
        font-size: ${rem(20)};
      }
    }
  }

  .product-entry-hardware__cards-container {
    z-index: 2;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: ${rem(16)};
    padding: ${rem(48)} 0 ${rem(80)};

    @media ${media.midUp} {
      padding-bottom: ${rem(104)};
      max-width: ${rem(512)};
    }
    .product-entry-hardware__card-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(160)};
      height: ${rem(184)};
      border-radius: ${rem(4)};
      background: var(--color-white);
      padding: ${rem(24)};
    }
  }
`
